/**
 * Skeleton.tsx — shimmer placeholders for loading states.
 *
 * `Skeleton` is a single tokened "bone". The presets below mirror the two
 * Inventory layouts (dense table + card grid) so the swap to real content is
 * seamless. Animation is disabled under `prefers-reduced-motion`.
 */

import { cn } from '@/utils/cn';

/** A single shimmer bone. Pass `className` for size/shape (h-*, w-*, rounded-*). */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn(
        'animate-pulse rounded bg-bg-subtle motion-reduce:animate-none',
        className,
      )}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  PRESET: dense table (mirrors InventoryListScreen list view — 7 columns)
// ─────────────────────────────────────────────────────────────────────────

export function SkeletonInventoryTable({ rows = 10 }: { rows?: number }) {
  return (
    <div className="flex-1 overflow-hidden rounded-lg border border-stroke-hairline/10 bg-bg-surface">
      <table className="w-full text-body">
        <thead className="sticky top-0 z-10 bg-bg-surface">
          <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
            <th className="px-4 py-2.5 text-left"><Skeleton className="h-3 w-16" /></th>
            <th className="px-4 py-2.5 text-left"><Skeleton className="h-3 w-24" /></th>
            <th className="px-4 py-2.5 text-right"><Skeleton className="ml-auto h-3 w-12" /></th>
            <th className="px-4 py-2.5 text-right"><Skeleton className="ml-auto h-3 w-12" /></th>
            <th className="px-4 py-2.5 text-left"><Skeleton className="h-3 w-12" /></th>
            <th className="px-4 py-2.5 text-right"><Skeleton className="ml-auto h-3 w-10" /></th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i} className="border-b border-stroke-hairline/5">
              <td className="px-4 py-3">
                <Skeleton className="h-3.5 w-40" />
              </td>
              <td className="px-4 py-3">
                <Skeleton className="h-3 w-24" />
              </td>
              <td className="px-4 py-3"><Skeleton className="ml-auto h-3.5 w-16" /></td>
              <td className="px-4 py-3"><Skeleton className="ml-auto h-3.5 w-10" /></td>
              <td className="px-4 py-3"><Skeleton className="h-3 w-10" /></td>
              <td className="px-4 py-3"><Skeleton className="ml-auto h-3 w-8" /></td>
              <td className="px-4 py-3"><Skeleton className="ml-auto h-5 w-12 rounded-md" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  PRESET: card grid (mirrors InventoryListScreen grid view)
// ─────────────────────────────────────────────────────────────────────────

export function SkeletonInventoryGrid({
  cards = 12,
  cols = 'grid-cols-2 md:grid-cols-3 xl:grid-cols-4',
}: {
  cards?: number;
  cols?: string;
}) {
  return (
    <div className="flex-1 overflow-hidden rounded-lg border border-stroke-hairline/10 bg-bg-surface p-3">
      <div className={cn('grid gap-3', cols)}>
        {Array.from({ length: cards }).map((_, i) => (
          <div
            key={i}
            className="flex flex-col gap-2 rounded-md border border-stroke-hairline/10 bg-bg-canvas p-3"
          >
            <div className="flex items-start gap-2">
              <Skeleton className="h-9 w-9 rounded" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-2.5 w-1/2" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Skeleton className="h-3.5 w-14" />
              <Skeleton className="h-2.5 w-10" />
            </div>
            <Skeleton className="mt-1 h-5 w-20 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}
